// src/components/DashboardMapRiskView.jsx
import { useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

function safeJsonParse(s) {
  if (!s || typeof s !== "string") return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeToFeatureCollection(geo, id, props = {}) {
  if (!geo) return null;
  let obj = geo;
  if (typeof geo === "string") obj = safeJsonParse(geo);
  if (!obj) return null;

  if (obj.type === "FeatureCollection" && Array.isArray(obj.features)) {
    return {
      type: "FeatureCollection",
      features: obj.features
        .filter(Boolean)
        .map((f, idx) => ({
          ...f,
          properties: { ...(f.properties || {}), ...props, __fid: String(id), __idx: idx },
        })),
    };
  }
  if (obj.type === "Feature" && obj.geometry) {
    return {
      type: "FeatureCollection",
      features: [{ ...obj, properties: { ...(obj.properties || {}), ...props, __fid: String(id) } }],
    };
  }
  if (obj.type && (obj.coordinates || obj.geometries)) {
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: obj, properties: { ...props, __fid: String(id) } }],
    };
  }
  return null;
}

function flattenCoords(coords, out) {
  if (!coords) return;
  if (typeof coords[0] === "number" && typeof coords[1] === "number") {
    out.push(coords);
    return;
  }
  for (const c of coords) flattenCoords(c, out);
}

function calcBboxOfGeometry(geom) {
  try {
    if (!geom) return null;
    const points = [];
    if (geom.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
      for (const g of geom.geometries) {
        if (g?.coordinates) flattenCoords(g.coordinates, points);
      }
    } else if (geom.coordinates) {
      flattenCoords(geom.coordinates, points);
    }
    if (!points.length) return null;

    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity;
    for (const [lng, lat] of points) {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      minLng = Math.min(minLng, lng);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      maxLat = Math.max(maxLat, lat);
    }
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    return [minLng, minLat, maxLng, maxLat];
  } catch {
    return null;
  }
}

export default function DashboardMapRiskView({
  height = 620,
  token,
  fields = [],
  selectedId,
  showAll = false,
  onSelect,
  onOpenField,
  styleUrl = SATELLITE_STYLE,
}) {
  const mapRef = useRef(null);
  const rootRef = useRef(null);

  const fcPolys = useMemo(() => {
    const out = [];
    for (const f of Array.isArray(fields) ? fields : []) {
      const id = f?.id ?? f?.fieldId;
      if (id == null) continue;

      const fc = normalizeToFeatureCollection(
        f?.geometryJson ?? f?.geometry ?? f?.geometry_json,
        id,
        {
          id: String(id),
          fieldId: String(id),
          fieldName: f?.fieldName ?? f?.name ?? `Поле #${id}`,
          riskLevel: String(f?.riskLevel ?? "").toUpperCase(), // ✅ manual override already in props
        }
      );

      if (fc?.features?.length) out.push(...fc.features);
    }
    return { type: "FeatureCollection", features: out };
  }, [fields]);

  function fitAll(map) {
    const bxs = [];
    for (const feat of fcPolys.features) {
      const bbox = calcBboxOfGeometry(feat.geometry);
      if (bbox) bxs.push(bbox);
    }
    if (!bxs.length) return;

    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity;
    for (const [a, b, c, d] of bxs) {
      minLng = Math.min(minLng, a);
      minLat = Math.min(minLat, b);
      maxLng = Math.max(maxLng, c);
      maxLat = Math.max(maxLat, d);
    }
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return;

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 70, duration: 900 }
    );
  }

  function fitOne(map, id) {
    const s = String(id);
    const feat = fcPolys.features.find((x) => String(x?.properties?.fieldId) === s);
    if (!feat) return;
    const bbox = calcBboxOfGeometry(feat.geometry);
    if (!bbox) return;
    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: 90, duration: 900 }
    );
  }

  useEffect(() => {
    if (!rootRef.current || mapRef.current) return;
    if (!token) return;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: rootRef.current,
      style: styleUrl,
      center: [31.0, 48.4],
      zoom: 5,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      addTerrainLayer(map);
      map.addSource("dash_fields", { type: "geojson", data: fcPolys });

      // fill by risk
      map.addLayer({
        id: "dash_fields_fill",
        type: "fill",
        source: "dash_fields",
        paint: {
          "fill-color": [
            "case",
            ["==", ["get", "riskLevel"], "HIGH"],
            "rgba(239,68,68,0.55)",
            ["==", ["get", "riskLevel"], "MEDIUM"],
            "rgba(245,158,11,0.55)",
            ["==", ["get", "riskLevel"], "LOW"],
            "rgba(34,197,94,0.50)",
            "rgba(148,163,184,0.35)",
          ],
          "fill-opacity": 0.75,
        },
      });

      map.addLayer({
        id: "dash_fields_line",
        type: "line",
        source: "dash_fields",
        paint: {
          "line-color": "rgba(255,255,255,0.75)",
          "line-width": 2,
          "line-opacity": 0.9,
        },
      });

      map.addLayer({
        id: "dash_fields_selected",
        type: "line",
        source: "dash_fields",
        filter: ["==", ["get", "fieldId"], String(selectedId || "")],
        paint: {
          "line-color": "rgba(255,255,255,0.98)",
          "line-width": 4,
          "line-opacity": 0.95,
        },
      });

      map.on("click", "dash_fields_fill", (e) => {
        const f0 = e?.features?.[0];
        const fid = f0?.properties?.fieldId ?? f0?.properties?.id;
        if (!fid) return;
        onSelect?.(String(fid));
      });

      map.on("dblclick", "dash_fields_fill", (e) => {
        const f0 = e?.features?.[0];
        const fid = f0?.properties?.fieldId ?? f0?.properties?.id;
        if (!fid) return;
        onOpenField?.(String(fid));
      });

      map.on("mouseenter", "dash_fields_fill", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "dash_fields_fill", () => (map.getCanvas().style.cursor = ""));

      if (showAll) fitAll(map);
      else if (selectedId) fitOne(map, selectedId);
      else fitAll(map);

      setTimeout(() => {
        try {
          map.resize();
        } catch {}
      }, 120);
    });

    mapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch {}
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource("dash_fields");
    if (!src) return;
    try {
      src.setData(fcPolys);
    } catch {}
  }, [fcPolys]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer("dash_fields_selected")) {
        map.setFilter("dash_fields_selected", ["==", ["get", "fieldId"], String(selectedId || "")]);
      }
    } catch {}
    if (!showAll && selectedId) fitOne(map, selectedId);
  }, [selectedId, showAll]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showAll) fitAll(map);
    else if (selectedId) fitOne(map, selectedId);
  }, [showAll]); // eslint-disable-line

  return (
    <div style={{ position: "relative", height, width: "100%", overflow: "hidden", borderRadius: 16 }}>
      <div ref={rootRef} style={{ position: "absolute", inset: 0 }} />

      {/* ✅ Legend */}
      <div
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          zIndex: 3,
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(10px)",
          padding: 10,
          color: "rgba(255,255,255,0.92)",
          fontWeight: 900,
          display: "grid",
          gap: 8,
          minWidth: 200,
        }}
      >
        <div style={{ fontWeight: 980 }}>⚠ Ризики</div>
        <LegendRow color="rgba(34,197,94,0.70)" label="Низький" />
        <LegendRow color="rgba(245,158,11,0.75)" label="Середній" />
        <LegendRow color="rgba(239,68,68,0.80)" label="Високий" />
        <LegendRow color="rgba(148,163,184,0.55)" label="Не обрано" />
        <div style={{ opacity: 0.75, fontWeight: 900, marginTop: 2 }}>
        </div>
      </div>

      <style>{`
        .mapboxgl-canvas{ border-radius: 16px !important; }
        .mapboxgl-map{ border-radius: 16px !important; }
      `}</style>
    </div>
  );
}

function LegendRow({ color, label }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 6,
          background: color,
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      />
      <span style={{ opacity: 0.92 }}>{label}</span>
    </div>
  );
}